import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

const template = document.createElement('template');
template.innerHTML = '<div></div>';

class SqmCalculatorComponent extends HTMLElement {
  constructor() {
    super();
    this.sqmPerBox = 1;
    this.variantPriceCents = 0;
    this.variantCompareCents = 0;
    this.minQuantity = 1;
    this.maxQuantity = null;
    this.step = 1;
    this.moneyFormat = '{{amount}}';
    this.currency = 'USD';
    this._baseBoxes = 1;
  }

  connectedCallback() {
    this.sqmPerBox = Number(this.dataset.sqmPerBox) || 1;
    this.variantPriceCents = Number(this.dataset.variantPrice) || 0;
    this.variantCompareCents = Number(this.dataset.variantComparePrice) || 0;
    this.minQuantity = Number(this.dataset.minQuantity) || 1;
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.currency = this.dataset.currency || 'USD';

    const boxesInput = this.querySelector('[data-boxes]');
    const sqmInput = this.querySelector('[data-sqm]');
    const form = this.closest('form');
    const quantityInput = form ? form.querySelector('[data-quantity-for-cart]') : null;
    const wastageToggle = this.querySelector('[data-wastage]');
    const totalPriceEl = this.querySelector('[data-total-price]');
    const savingsEl = this.querySelector('[data-savings]');
    const savingsAmountEl = this.querySelector('[data-savings-amount]');
    const boxesMinus = this.querySelector('[ref="boxesMinus"]');
    const boxesPlus = this.querySelector('[ref="boxesPlus"]');

    if (!boxesInput || !sqmInput) return;

    this.boxesInput = boxesInput;
    this.sqmInput = sqmInput;
    this.quantityInput = quantityInput;
    this.wastageToggle = wastageToggle;
    this.totalPriceEl = totalPriceEl;
    this.savingsEl = savingsEl;
    this.savingsAmountEl = savingsAmountEl;
    this.boxesMinus = boxesMinus;
    this.boxesPlus = boxesPlus;

    this.maxQuantity = boxesInput.max ? Number(boxesInput.max) : null;
    this.step = Number(boxesInput.step) || 1;
    this._baseBoxes = this.minQuantity;

    this._onBoxesInput = () => this.handleBoxesInput();
    this._onBoxesChange = () => this.handleBoxesChange();
    this._onSqmChange = () => this.handleSqmChange();
    this._onWastageChange = () => this.updateDisplay();
    this._onVariantUpdate = (e) => this.onVariantUpdate(e);

    boxesInput.addEventListener('input', this._onBoxesInput);
    boxesInput.addEventListener('change', this._onBoxesChange);
    sqmInput.addEventListener('change', this._onSqmChange);
    if (wastageToggle) wastageToggle.addEventListener('change', this._onWastageChange);

    if (boxesMinus) boxesMinus.addEventListener('click', () => this.stepBoxes(-1));
    if (boxesPlus) boxesPlus.addEventListener('click', () => this.stepBoxes(1));

    document.addEventListener(ThemeEvents.variantUpdate, this._onVariantUpdate);

    this.initPanels();
    this.updateDisplay();
  }

  disconnectedCallback() {
    if (this.boxesInput) this.boxesInput.removeEventListener('input', this._onBoxesInput);
    if (this.boxesInput) this.boxesInput.removeEventListener('change', this._onBoxesChange);
    if (this.sqmInput) this.sqmInput.removeEventListener('change', this._onSqmChange);
    if (this.wastageToggle) this.wastageToggle.removeEventListener('change', this._onWastageChange);
    document.removeEventListener(ThemeEvents.variantUpdate, this._onVariantUpdate);
  }

  /* ---------- Panels (calculator / delivery) ---------- */

  initPanels() {
    this.panels = this.querySelectorAll('[data-panel]');

    this.querySelectorAll('[data-panel-toggle]').forEach(btn => {
      btn.addEventListener('click', () => this.togglePanel(btn.dataset.panelToggle));
    });

    this.querySelectorAll('[data-panel-close]').forEach(btn => {
      btn.addEventListener('click', () => this.closePanel(btn.dataset.panelClose));
    });

    const updateBtn = this.querySelector('[data-update-from-dims]');
    if (updateBtn) updateBtn.addEventListener('click', () => this.updateFromDimensions());
  }

  togglePanel(name) {
    const target = this.querySelector(`[data-panel="${name}"]`);
    if (!target) return;
    const isActive = target.classList.contains('is-active');

    this.panels.forEach(p => p.classList.remove('is-active'));

    if (!isActive) target.classList.add('is-active');
  }

  closePanel(name) {
    const panel = this.querySelector(`[data-panel="${name}"]`);
    if (panel) panel.classList.remove('is-active');
  }

  updateFromDimensions() {
    const w = parseFloat(this.querySelector('[data-dim-width]')?.value) || 0;
    const h = parseFloat(this.querySelector('[data-dim-height]')?.value) || 0;
    const sqm = w * h;
    if (sqm <= 0) return;
    this._baseBoxes = this.clampBoxes(Math.ceil(sqm / this.sqmPerBox));
    this.updateDisplay();
  }

  /* ---------- Core quantity logic ---------- */

  clampBoxes(n) {
    n = Math.max(this.minQuantity, Math.round(n) || this.minQuantity);
    if (this.maxQuantity != null) n = Math.min(n, this.maxQuantity);
    return n;
  }

  get quantityForCart() {
    const b = this._baseBoxes;
    if (this.wastageToggle?.checked) {
      const wastageBoxes = b * 0.1;
      const extraBoxes = Math.max(1, Math.ceil(wastageBoxes - 1e-9));
      return b + extraBoxes;
    }
    return b;
  }

  formatSqm(sqm) {
    return sqm % 1 === 0 ? String(sqm) : sqm.toFixed(2);
  }

  handleBoxesInput() {
    const v = parseInt(this.boxesInput.value, 10);
    if (Number.isFinite(v) && v >= this.minQuantity) {
      this._baseBoxes = this.clampBoxes(v);
    }
    const baseSqm = this._baseBoxes * this.sqmPerBox;
    this.sqmInput.value = this.formatSqm(baseSqm);
    if (this.quantityInput) this.quantityInput.value = String(this.quantityForCart);
    this.updateTotal();
  }

  handleBoxesChange() {
    const v = parseInt(this.boxesInput.value, 10);
    this._baseBoxes = this.clampBoxes(Number.isFinite(v) ? v : this.minQuantity);
    this.updateDisplay();
  }

  handleSqmChange() {
    const s = parseFloat(this.sqmInput.value);
    const sqm = Number.isFinite(s) && s >= 0 ? s : 0;
    this._baseBoxes = this.clampBoxes(Math.round(sqm / this.sqmPerBox));
    this.updateDisplay();
  }

  stepBoxes(delta) {
    const step = this.step || 1;
    this._baseBoxes = this.clampBoxes(this._baseBoxes + delta * step);
    this.updateDisplay();
  }

  updateDisplay() {
    const qty = this.quantityForCart;
    const totalSqm = qty * this.sqmPerBox;

    this.boxesInput.value = String(qty);
    this.sqmInput.value = this.formatSqm(totalSqm);
    if (this.quantityInput) this.quantityInput.value = String(qty);
    this.updateTotal();
  }

  updateTotal() {
    if (!this.totalPriceEl) return;
    const qty = this.quantityForCart;
    const totalCents = this.variantPriceCents * qty;
    this.totalPriceEl.textContent = formatMoney(totalCents, this.moneyFormat, this.currency);
    this.updateSavings(qty);
  }

  updateSavings(qty) {
    if (!this.savingsEl || !this.savingsAmountEl) return;
    const onSale = this.variantCompareCents > this.variantPriceCents && this.variantCompareCents > 0;
    if (!onSale) {
      this.savingsEl.classList.add('sqm-calculator__savings--hidden');
      return;
    }
    const savedPerBoxCents = this.variantCompareCents - this.variantPriceCents;
    const totalSavedCents = savedPerBoxCents * qty;
    this.savingsAmountEl.textContent = formatMoney(totalSavedCents, this.moneyFormat, this.currency);
    this.savingsEl.classList.remove('sqm-calculator__savings--hidden');
  }

  onVariantUpdate(event) {
    const productId = event.detail?.data?.productId;
    const form = this.closest('product-form-component');
    if (form && productId && form.dataset.productId !== productId) return;
    const resource = event.detail?.resource;
    if (resource != null && typeof resource.price === 'number') {
      this.variantPriceCents = resource.price;
      const cmp = resource.compare_at_price != null ? resource.compare_at_price : resource.compareAtPrice;
      this.variantCompareCents = Number(cmp) || 0;
      this.updateTotal();
    }
  }
}

if (!customElements.get('sqm-calculator-component')) {
  customElements.define('sqm-calculator-component', SqmCalculatorComponent);
}
